// packages/web/src/pages/Super/components/CreateUpdateReferralProvider.tsx
import { useState } from 'react'
import { Button, Group, Select, Stack, TextInput, Title } from '@mantine/core'
import { useForm } from '@mantine/form'
import { notifications } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import { logError } from '@/utils/errorUtils'
import { NUCC_CLASSIFICATIONS } from '@/pages/Patient/components/OrderTests/shared/nuccClassifications'
import { AddressFieldsGroup, addressValidation, normaliseZip } from './shared/AddressFieldsGroup'
import { PhoneFaxFieldsGroup, tenDigitValidation } from './shared/PhoneFaxFieldsGroup'
import { geocodeAddress } from './shared/geocodeAddress'

const CREDENTIAL_OPTIONS = ['MD', 'DO', 'NP', 'PA'].map((credential) => ({
  value: credential,
  label: credential,
}))

interface CreateUpdateReferralProviderProps {
  onSave?: () => void
  onCancel?: () => void
}

interface FormValues {
  practitioner: {
    firstName: string
    lastName: string
    credentials: string
    npi: string
    specialtyCode: string
  }
  clinic: {
    name: string
    orgNpi: string
    phone: string
    fax: string
    website: string
    address: {
      street: string
      city: string
      state: string
      zipCode: string
    }
  }
}

export function CreateUpdateReferralProvider({
  onSave,
  onCancel,
}: CreateUpdateReferralProviderProps) {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const [isLoading, setIsLoading] = useState(false)

  const form = useForm<FormValues>({
    initialValues: {
      practitioner: {
        firstName: '',
        lastName: '',
        credentials: '',
        npi: '',
        specialtyCode: '',
      },
      clinic: {
        name: '',
        orgNpi: '',
        phone: '',
        fax: '',
        website: '',
        address: { street: '', city: '', state: '', zipCode: '' },
      },
    },
    validate: {
      practitioner: {
        firstName: (v) => (!v?.trim() ? 'First name is required' : null),
        lastName: (v) => (!v?.trim() ? 'Last name is required' : null),
        npi: (v) => {
          if (!v?.trim()) return 'NPI is required'
          const digits = v.replace(/\D/g, '')
          return digits.length === 10 ? null : 'NPI must be 10 digits'
        },
        specialtyCode: (v) => (!v ? 'Specialty is required' : null),
      },
      clinic: {
        orgNpi: (v) => {
          if (!v?.trim()) return null
          const digits = v.replace(/\D/g, '')
          return digits.length === 10 ? null : 'Organization NPI must be 10 digits'
        },
        phone: tenDigitValidation,
        fax: tenDigitValidation,
        address: addressValidation,
      },
    },
  })

  const handleSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      const zip = normaliseZip(values.clinic.address.zipCode)
      const npiDigits = values.practitioner.npi.replace(/\D/g, '')

      const orgNpiDigits = values.clinic.orgNpi.replace(/\D/g, '')

      // Geocode before submit so distance-sorted provider search works immediately.
      // geocodeAddress never throws — a failure just leaves coords off the payload.
      const coords = await geocodeAddress(medplum, {
        street: values.clinic.address.street.trim(),
        city: values.clinic.address.city.trim(),
        state: values.clinic.address.state,
        zip,
      })

      const payload = {
        practitioner: {
          firstName: values.practitioner.firstName.trim(),
          lastName: values.practitioner.lastName.trim(),
          credentials: values.practitioner.credentials.trim() || undefined,
          npi: npiDigits,
          specialtyCode: values.practitioner.specialtyCode,
        },
        clinic: {
          name: values.clinic.name.trim() || undefined,
          orgNpi: orgNpiDigits || undefined,
          street: values.clinic.address.street.trim(),
          city: values.clinic.address.city.trim(),
          state: values.clinic.address.state,
          zip,
          phone: values.clinic.phone.trim() || undefined,
          fax: values.clinic.fax.trim() || undefined,
          website: values.clinic.website.trim() || undefined,
          lat: coords?.lat,
          lon: coords?.lon,
        },
      }

      const result = await middleware.post('/super/create-referral-provider', payload)

      notifications.show({
        title: result.matched
          ? 'Provider already existed'
          : result.pending === true
            ? 'Provider submitted for approval'
            : 'Referral provider created',
        message: result.matched
          ? 'An NPI match was found; the existing practitioner was linked to this new clinic.'
          : result.pending === true
            ? 'Pending admin approval — you can save a draft order in the meantime.'
            : 'New referral provider saved.',
        color: 'green',
      })
      onSave?.()
    } catch (error) {
      logError('super', 'Failed to create referral provider', error)
      notifications.show({
        title: 'Error',
        message: 'Failed to create referral provider. Please try again.',
        color: 'red',
      })
    } finally {
      setIsLoading(false)
    }
  }

  const specialtyOptions = NUCC_CLASSIFICATIONS.map((c) => ({
    value: c.code,
    label: c.label,
  }))

  return (
    <>
      <Title order={2} mb="md">
        Add Referral Provider
      </Title>

      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack gap="md">
          <Title order={4}>Practitioner</Title>
          <Group grow>
            <TextInput
              label="First Name"
              {...form.getInputProps('practitioner.firstName')}
              required
            />
            <TextInput
              label="Last Name"
              {...form.getInputProps('practitioner.lastName')}
              required
            />
          </Group>
          <Group grow>
            <Select
              label="Credentials"
              placeholder="Select credentials"
              data={CREDENTIAL_OPTIONS}
              clearable
              {...form.getInputProps('practitioner.credentials')}
            />
            <TextInput
              label="NPI"
              placeholder="10 digits"
              {...form.getInputProps('practitioner.npi')}
              required
            />
          </Group>
          <Select
            label="Specialty"
            placeholder="Select specialty"
            data={specialtyOptions}
            {...form.getInputProps('practitioner.specialtyCode')}
            required
            searchable
          />

          <Title order={4} mt="md">
            Clinic / Location
          </Title>
          <Group grow>
            <TextInput
              label="Clinic Name (optional)"
              placeholder="Leave blank to use practitioner name"
              {...form.getInputProps('clinic.name')}
            />
            <TextInput
              label="Organization NPI (optional)"
              placeholder="Type-2 NPI, if the clinic has one"
              {...form.getInputProps('clinic.orgNpi')}
            />
          </Group>
          <AddressFieldsGroup form={form} prefix="clinic.address" />
          <PhoneFaxFieldsGroup
            phoneValue={form.values.clinic.phone}
            faxValue={form.values.clinic.fax}
            phoneError={form.errors['clinic.phone']}
            faxError={form.errors['clinic.fax']}
            onPhoneChange={(v) => form.setFieldValue('clinic.phone', v)}
            onFaxChange={(v) => form.setFieldValue('clinic.fax', v)}
          />
          <TextInput label="Website (optional)" {...form.getInputProps('clinic.website')} />

          <Group justify="flex-end" mt="xl">
            {onCancel && (
              <Button variant="subtle" onClick={onCancel}>
                Cancel
              </Button>
            )}
            <Button type="submit" loading={isLoading} disabled={isLoading}>
              Add Provider
            </Button>
          </Group>
        </Stack>
      </form>
    </>
  )
}
